import * as React from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { SparkLineChart } from '@mui/x-charts/SparkLineChart';
import { areaElementClasses } from '@mui/x-charts/LineChart';

// This new function generates date labels for the last 30 days dynamically.
function getLast30DaysLabels() {
  const labels = [];
  const today = new Date();
  for (let i = 29; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(today.getDate() - i);
    // Formats the date to a "Mon Day" string, e.g., "Apr 22"
    const monthName = date.toLocaleDateString('en-US', { month: 'short' });
    const day = date.getDate();
    labels.push(`${monthName} ${day}`);
  }
  return labels;
}

function AreaGradient({ color, id }) {
  return (
    <defs>
      <linearGradient id={id} x1="50%" y1="0%" x2="50%" y2="100%">
        <stop offset="0%" stopColor={color} stopOpacity={0.3} />
        <stop offset="100%" stopColor={color} stopOpacity={0} />
      </linearGradient>
    </defs>
  );
}

AreaGradient.propTypes = {
  color: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
};

// --- (DELETED) ---
// The local frontend calculation functions (calculateTrendPercentage, 
// determineTrend, formatTrendValue) have been removed.
// --- (DELETED) ---

// --- (MODIFIED) ---
// The component now accepts 'trend' and 'trendLabel' directly from the API props.
function StatCard({ title, value, interval, trend, data, trendLabel }) {
  const theme = useTheme();
  const last30Days = getLast30DaysLabels();

  const trendColors = {
    up:
      theme.palette.mode === 'light'
        ? theme.palette.error.main
        : theme.palette.error.dark,
    down:
      theme.palette.mode === 'light'
        ? theme.palette.success.main
        : theme.palette.success.dark,
    neutral:
      theme.palette.mode === 'light'
        ? theme.palette.grey[400]
        : theme.palette.grey[700],
  };

  const labelColors = {
    up: 'error',
    down: 'success',
    neutral: 'default',
  };
  
  // Use the 'trend' prop from the backend to determine colors. Fallback to neutral.
  const color = labelColors[trend] || 'default';
  const chartColor = trendColors[trend] || trendColors.neutral;

  return (
    <Card variant="outlined" sx={{ height: '100%', flexGrow: 1 }}>
      <CardContent>
        <Typography component="h2" variant="subtitle2" gutterBottom>
          {title}
        </Typography>
        <Stack
          direction="column"
          sx={{ justifyContent: 'space-between', flexGrow: '1', gap: 1 }}
        >
          <Stack sx={{ justifyContent: 'space-between' }}>
            <Stack
              direction="row"
              sx={{ justifyContent: 'space-between', alignItems: 'center' }}
            >
              <Typography variant="h4" component="p">
                {value}
              </Typography>
              {/* --- (MODIFIED) Use the trendLabel directly from props --- */}
              <Chip size="small" color={color} label={trendLabel} />
            </Stack>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {interval}
            </Typography>
          </Stack>
          <Box sx={{ width: '100%', height: 50 }}>
            <SparkLineChart
              colors={[chartColor]}
              data={data}
              area
              showHighlight
              showTooltip
              xAxis={{
                scaleType: 'band',
                data: last30Days,
              }}
              sx={{
                [`& .${areaElementClasses.root}`]: {
                  fill: `url(#area-gradient-${value})`,
                },
              }}
            >
              <AreaGradient color={chartColor} id={`area-gradient-${value}`} />
            </SparkLineChart>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

StatCard.propTypes = {
  data: PropTypes.arrayOf(PropTypes.number), // Can contain nulls now
  interval: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  trend: PropTypes.oneOf(['down', 'neutral', 'up']),
  value: PropTypes.string.isRequired,
  // --- (ADDED) Add the new prop for validation ---
  trendLabel: PropTypes.string,
};

// --- (ADDED) Provide a default value for safety ---
StatCard.defaultProps = {
  trendLabel: '+0.0%',
  trend: 'neutral',
  data: Array(30).fill(null),
};


export default StatCard;